import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import { Dispatch } from 'redux';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from 'grommet';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Paper } from '../../../pulsechain_components/Paper';
import { Text } from '../../../pulsechain_components/Text';
import { Heading } from '../../../pulsechain_components/Heading';
import { Status } from './Status';
import { ActionButton } from './ActionButton';
import { DepositKeyInterface, StoreState } from '../../../store/reducers';
import { handleMultipleTransactions } from '../transactionUtils';
import { useWeb3ReactLegacy } from '../../../utils/web3-compat';
import {
  DepositStatus,
  DispatchTransactionStatusUpdateType,
  TransactionStatus,
  updateTransactionStatus,
  updateBeaconDepositStatus,
  DispatchBeaconDepositStatusUpdateType,
  updateValidatorDepositStatus,
  DispatchValidatorDepositStatusUpdateType,
  ValidatorDepositStatus,
} from '../../../store/actions/depositFileActions';
import { FormattedMessage, useIntl } from 'react-intl';
import { DepositStatusTracker } from '../../../components/DepositStatusTracker';
import { ValidatorStatusDisplay } from '../../../components/ValidatorStatusDisplay';
import {
  DepositQueueStatus,
  BeaconDepositStatus,
} from '../../../utils/depositQueueTracker';
import { checkValidatorsStatus } from '../../../utils/validatorStatusChecker';

const CustomTableRow = styled(TableRow)<{
  header?: boolean;
  invalid?: boolean;
}>`
  background-color: ${p => p.theme.gray.dark};
`;

const CustomPaper = styled(Paper)`
  display: block;
  height: 280px;
  overflow: auto;
`;

const CustomTable = styled(Table)`
  width: 100%;
  @media only screen and (max-width: ${p => p.theme.screenSizes.medium}) {
    td {
      padding: 5px;
    }
  }
`;

const BeaconStatusRow = styled(TableRow)`
  background-color: ${p => p.theme.gray.dark};
  td {
    padding-top: 0 !important;
    border-bottom: 1px solid ${p => p.theme.gray.medium};
  }
`;

// Prop definitions
interface OwnProps {}
interface StateProps {
  depositKeys: DepositKeyInterface[];
}
interface DispatchProps {
  dispatchTransactionStatusUpdate: DispatchTransactionStatusUpdateType;
  dispatchBeaconDepositStatusUpdate: DispatchBeaconDepositStatusUpdateType;
  dispatchValidatorDepositStatusUpdate: DispatchValidatorDepositStatusUpdateType;
}
type Props = StateProps & DispatchProps & OwnProps;

const _KeyList = ({
  depositKeys,
  dispatchTransactionStatusUpdate,
  dispatchBeaconDepositStatusUpdate,
  dispatchValidatorDepositStatusUpdate,
}: Props) => {
  const { account, provider } = useWeb3ReactLegacy();
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const hasCheckedInitialStatus = useRef(false);

  // Check validator status on initial load
  useEffect(() => {
    if (hasCheckedInitialStatus.current || depositKeys.length === 0) {
      return;
    }

    const checkInitialStatus = async () => {
      setIsCheckingStatus(true);
      hasCheckedInitialStatus.current = true;

      try {
        const pubkeys = depositKeys.map(k => k.pubkey);
        const statusResults = await checkValidatorsStatus(pubkeys);

        statusResults.forEach((result, pubkey) => {
          dispatchValidatorDepositStatusUpdate({
            pubkey,
            validatorDepositStatus: result.status,
            beaconStatus: result.beaconStatus,
            beaconIndex: result.beaconIndex,
            beaconBalance: result.beaconBalance,
            execDeposited: result.execDeposited,
            execTxHash: result.execTxHash,
            execBlockNumber: result.execBlockNumber,
            execDepositIndex: result.execDepositIndex,
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to check initial validator status:', error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkInitialStatus();
  }, [depositKeys, dispatchValidatorDepositStatusUpdate]);

  const handleActionClick = (depositKey: DepositKeyInterface): void => {
    if (!provider) {
      // eslint-disable-next-line no-console
      console.error('No provider available for transaction');
      return;
    }
    if (!account) {
      // eslint-disable-next-line no-console
      console.error('No account connected');
      return;
    }
    handleMultipleTransactions(
      [depositKey],
      provider,
      account,
      dispatchTransactionStatusUpdate
    );
  };

  const handleStatusUpdate = useCallback(
    (pubkey: string, status: DepositQueueStatus) => {
      dispatchBeaconDepositStatusUpdate({
        pubkey,
        beaconDepositStatus: status.status,
        depositIndex: status.depositIndex,
        beaconProcessingIndex: status.beaconProcessingIndex,
        queuePosition: status.queuePosition,
        validatorIndex: status.validatorIndex,
      });
    },
    [dispatchBeaconDepositStatusUpdate]
  );

  const { formatMessage } = useIntl();
  return (
    <CustomPaper className="mt20">
      <Heading level={3} size="small" color="blueMedium" className="mb20">
        <FormattedMessage defaultMessage="Key list" />
      </Heading>
      <Box pad="x-small">
        <CustomTable>
          <TableHeader>
            <CustomTableRow header>
              <TableCell scope="col" border="bottom">
                <FormattedMessage defaultMessage="Validator public key" />
              </TableCell>
              <TableCell scope="col" border="bottom">
                <FormattedMessage defaultMessage="Status" />
              </TableCell>
              <TableCell scope="col" border="bottom">
                <FormattedMessage defaultMessage="Action" />
              </TableCell>
            </CustomTableRow>
          </TableHeader>
          <TableBody>
            {depositKeys.map((depositKey, i) => {
              const {
                pubkey,
                transactionStatus,
                txHash,
                depositStatus,
                beaconDepositStatus,
                validatorDepositStatus,
                beaconStatus,
                beaconBalance,
                validatorIndex,
                execDeposited,
                execTxHash,
                execBlockNumber,
                execDepositIndex,
              } = depositKey;

              // Compute effective status - override NOT_DEPOSITED when tx succeeded
              const effectiveValidatorStatus =
                transactionStatus === TransactionStatus.SUCCEEDED &&
                validatorDepositStatus === ValidatorDepositStatus.NOT_DEPOSITED
                  ? ValidatorDepositStatus.DEPOSITED_WAITING
                  : validatorDepositStatus;

              // Show comprehensive status for validators that have been deposited (via tx or detected)
              const showComprehensiveStatus =
                (transactionStatus === TransactionStatus.SUCCEEDED ||
                  effectiveValidatorStatus ===
                    ValidatorDepositStatus.DEPOSITED_WAITING ||
                  effectiveValidatorStatus ===
                    ValidatorDepositStatus.PENDING_INIT ||
                  effectiveValidatorStatus ===
                    ValidatorDepositStatus.PENDING_QUEUED ||
                  effectiveValidatorStatus === ValidatorDepositStatus.ACTIVE) &&
                depositStatus !== DepositStatus.ALREADY_DEPOSITED;

              // Legacy beacon status tracker for post-transaction monitoring
              const showLegacyBeaconStatus =
                transactionStatus === TransactionStatus.SUCCEEDED &&
                depositStatus !== DepositStatus.ALREADY_DEPOSITED &&
                txHash &&
                beaconDepositStatus !== BeaconDepositStatus.ACTIVE &&
                !showComprehensiveStatus;

              return (
                <React.Fragment key={pubkey}>
                  <CustomTableRow
                    data-for={`double-deposit-${i}`}
                    data-tip={formatMessage({
                      defaultMessage:
                        'Your initial deposit has already been made for this validator public key. Please wait and check the status of your deposit on the Beaconchain data provider.',
                    })}
                    invalid={depositStatus === DepositStatus.ALREADY_DEPOSITED}
                  >
                    <TableCell>
                      <Text className="dont-break-out">
                        {`${pubkey.slice(0, 10)}...${pubkey.slice(-10)}`}
                      </Text>
                    </TableCell>
                    <TableCell>
                      {depositStatus === DepositStatus.ALREADY_DEPOSITED && (
                        <ReactTooltip
                          id={`double-deposit-${i}`}
                          place="top"
                          effect="solid"
                        />
                      )}
                      <Status
                        transactionStatus={transactionStatus}
                        depositStatus={depositStatus}
                      />
                    </TableCell>
                    <TableCell>
                      <ActionButton
                        onClick={() => handleActionClick(depositKey)}
                        transactionStatus={transactionStatus}
                        depositStatus={depositStatus}
                        validatorDepositStatus={effectiveValidatorStatus}
                        txHash={txHash}
                        pubkey={pubkey}
                      />
                    </TableCell>
                  </CustomTableRow>
                  {showComprehensiveStatus && (
                    <BeaconStatusRow>
                      <TableCell colSpan={3}>
                        <ValidatorStatusDisplay
                          status={effectiveValidatorStatus}
                          beaconStatus={beaconStatus}
                          beaconIndex={validatorIndex}
                          beaconBalance={beaconBalance}
                          execDeposited={
                            execDeposited ||
                            transactionStatus === TransactionStatus.SUCCEEDED
                          }
                          execTxHash={execTxHash || txHash}
                          execBlockNumber={execBlockNumber}
                          execDepositIndex={execDepositIndex}
                          pubkey={pubkey}
                          isLoading={isCheckingStatus}
                        />
                      </TableCell>
                    </BeaconStatusRow>
                  )}
                  {showLegacyBeaconStatus && (
                    <BeaconStatusRow>
                      <TableCell colSpan={3}>
                        <DepositStatusTracker
                          txHash={txHash}
                          pubkey={pubkey}
                          onStatusUpdate={status =>
                            handleStatusUpdate(pubkey, status)
                          }
                        />
                      </TableCell>
                    </BeaconStatusRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </CustomTable>
      </Box>
    </CustomPaper>
  );
};

const mapStateToProps = ({ depositFile }: StoreState): StateProps => {
  return { depositKeys: depositFile.keys };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  dispatchTransactionStatusUpdate: (pubkey, status, txHash) =>
    dispatch(updateTransactionStatus(pubkey, status, txHash)),
  dispatchBeaconDepositStatusUpdate: payload =>
    dispatch(updateBeaconDepositStatus(payload)),
  dispatchValidatorDepositStatusUpdate: payload =>
    dispatch(updateValidatorDepositStatus(payload)),
});

export const KeyList = connect<StateProps, DispatchProps, OwnProps, StoreState>(
  mapStateToProps,
  mapDispatchToProps
)(_KeyList);
